import './css/CommitTools.css';

import arrow from '../assets/images/tool_arrow.png';
import cursor from '../assets/images/tool_cursor.png';
import eraser from '../assets/images/tool_eraser.png';
import pen from '../assets/images/tool_pen.png';
import text from '../assets/images/tool_text.png';
import upload from '../assets/images/tool_upload.png';
import lock from '../assets/images/tool_lock.png';
import trash from '../assets/images/tool_delete.png';
import shuffle from '../assets/images/tool_random.png';

import { useState, useEffect, useRef } from 'react';

// const TOOLS = [cursor, pen, eraser, text, upload];
const TOOLS = [cursor, pen, eraser];

const BACKEND_URL = process.env.REACT_APP_API_URL;

const CommitTools = ({ availableYears, selectedYear, onYearChange, selectedTool, setSelectedTool, selectedLevel, setSelectedLevel, circleSize, setCircleSize, commitReady, handleSubmitCommits, clearCanvas, formattedCommits, userRepos, setSelectedRepoData, handleShuffle, starredRepos, setStarredRepos, showRepoDropdown, setShowRepoDropdown}) => {
    const drawOptionsRef = useRef(null)
    const shuffleOptionsRef = useRef(null)
    const repoDropdownRef = useRef(null)

    const[hideTools, setHideTools] = useState(false)
    const[hideConfirm, setHideConfirm] = useState(true)
    const[hideLoading, setHideLoading] = useState(true)

    const [loadingDone, setLoadingDone] = useState(false)
    const [loadingGood, setloadingGood] = useState(true)
    
    // activeTooltip: 1 (pen), 2 (eraser), or 'shuffle'
    const [activeTooltip, setActiveTooltip] = useState(null); 
    const [lastActiveDrawTool, setLastActiveDrawTool] = useState(1); // Track last active draw tool for fade out
    const hoverTimeoutRef = useRef(null);
    
    const [selectedRepo, setSelectedRepo] = useState('') // State for selected repository ID
    const [shuffleIntensity, setShuffleIntensity] = useState(5);



    const handleMouseEnter = (tooltipId) => {
        if (hoverTimeoutRef.current) {
            clearTimeout(hoverTimeoutRef.current);
            hoverTimeoutRef.current = null;
        }
        setActiveTooltip(tooltipId);
        
        // Update last active draw tool if applicable
        if (tooltipId === 1 || tooltipId === 2) {
            setLastActiveDrawTool(tooltipId);
        }
    };

    const handleMouseLeave = () => {
        if (hoverTimeoutRef.current) {
            clearTimeout(hoverTimeoutRef.current);
        }
        hoverTimeoutRef.current = setTimeout(() => {
            setActiveTooltip(null);
        }, 300); // 300ms timeout
    };

    const handleYearChange = (e) => {
        const newYear = e.target.value;
        if (onYearChange) {
            onYearChange(newYear);
        }
    };

    const handleToolClick = (index) => {
        setSelectedTool(index);
        // Don't close tooltip on click, let hover handle it
    };

    const handleLevelClick = (level) => {
        setSelectedLevel(level);
    };

    const handleRepoSelect = (repoId) => {
        setSelectedRepo(repoId);
        setShowRepoDropdown(false);
        
        // Find and set the complete repo data
        if (repoId && userRepos) {
            const repoData = userRepos.find(repo => repo.id.toString() === repoId.toString());
            setSelectedRepoData(repoData);
        } else {
            setSelectedRepoData(null);
        }
    };

    const handleStarRepo = async (e, repoId) => {
        e.stopPropagation();
        const sessionToken = localStorage.getItem('session_token');
        if (!sessionToken) return;

        // Optimistic update
        const newStarred = [...(starredRepos || []), repoId.toString()];
        setStarredRepos(newStarred);

        try {
            await fetch(`${BACKEND_URL}/github/starRepo`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${sessionToken}`
                },
                body: JSON.stringify({ repo_id: repoId.toString() })
            });
        } catch (error) {
            console.error('Error starring repo:', error);
            // Revert on error
            setStarredRepos(starredRepos);
        }
    };

    const handleUnstarRepo = async (e, repoId) => {
        e.stopPropagation();
        const sessionToken = localStorage.getItem('session_token');
        if (!sessionToken) return;

        // Optimistic update
        const newStarred = (starredRepos || []).filter(id => id !== repoId.toString());
        setStarredRepos(newStarred);

        try {
            await fetch(`${BACKEND_URL}/github/unstarRepo`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${sessionToken}`
                },
                body: JSON.stringify({ repo_id: repoId.toString() })
            });
        } catch (error) {
            console.error('Error unstarring repo:', error);
            // Revert on error
            setStarredRepos(starredRepos);
        }
    };

    const openConfirmation = () => {
        if (commitReady) {
            setHideConfirm(false)
            setHideTools(true)
            setHideLoading(true)
        }
    }
    
    const openTools = () => {
        setHideTools(false)
        setHideConfirm(true)
        setHideLoading(true)
    }

    const openLoading = () => {
        setHideTools(true)
        setHideConfirm(true)
        setHideLoading(false)
    }

    const submit = async () => {
        openLoading()
        handleSubmitCommits().then( data =>{
            setLoadingDone(true)
            // setTimeout(()=>{
            //     setLoadingDone(false)
            //     openTools()
            // }, 800)
        })
    }

    const applyShuffle = () => {
        if (handleShuffle) {
            handleShuffle(shuffleIntensity);
            setActiveTooltip(null);
        }
    }

    // Set default repository when userRepos loads
    useEffect(() => {
        if (userRepos && userRepos.length > 0 && !selectedRepo && starredRepos !== null) {
            let defaultRepo;
            
            // Try to find the first starred repo
            if (starredRepos.length > 0) {
                const firstStarredId = starredRepos[0];
                defaultRepo = userRepos.find(r => r.id.toString() === firstStarredId.toString());
            }
            
            // Fallback to first repo if no starred repo found or matched
            if (!defaultRepo) {
                defaultRepo = userRepos[0];
            }

            if (defaultRepo) {
                setSelectedRepo(defaultRepo.id.toString());
                setSelectedRepoData(defaultRepo);
            }
        }
    }, [userRepos, starredRepos, selectedRepo]);

    // Update draw options position when activeTooltip changes
    useEffect(() => {
        if (activeTooltip === 1 || activeTooltip === 2) {
            const drawOptions = drawOptionsRef.current;
            const toolIndex = activeTooltip;
            const toolEl = document.querySelector(`.tool[data-index="${toolIndex}"]`);
            
            if (drawOptions && toolEl) {
                const toolRect = toolEl.getBoundingClientRect();
                const containerRect = toolEl.offsetParent.getBoundingClientRect(); // offsetParent is .commit-tools
                
                // Calculate center alignment
                // Position of tool relative to container
                const toolRelativeLeft = toolRect.left - containerRect.left;
                
                // Center of the tool relative to container
                const toolCenter = toolRelativeLeft + (toolRect.width / 2);
                
                // Center the tooltip (subtract half its width)
                const leftOffset = toolCenter - (drawOptions.offsetWidth / 2);
                
                drawOptions.style.left = `${leftOffset}px`;
                
                // Add active class for animation
                requestAnimationFrame(() => {
                    drawOptions.classList.add('active');
                });
            }
        }
    }, [activeTooltip, lastActiveDrawTool]);

    useEffect(()=>{
        const handleMouseDown = (e) => {
            // Don't close if clicking inside shuffle options or draw options
            if ((shuffleOptionsRef.current && shuffleOptionsRef.current.contains(e.target)) || 
                (drawOptionsRef.current && drawOptionsRef.current.contains(e.target))) {
                return;
            }
            // Don't close if clicking the shuffle button itself
            if (e.target.closest('.shuffle-btn')) {
                return;
            }

            // Close repo dropdown if clicking outside
            if (repoDropdownRef.current && !repoDropdownRef.current.contains(e.target)) {
                setShowRepoDropdown(false);
            }

            // If clicking outside, we might want to close tooltips?
            // But hover logic handles closing. 
            
            if (!hideLoading) {
                setLoadingDone(false)
                openTools()
            }
        }
        window.addEventListener('mousedown', handleMouseDown);
        return () => {
            window.removeEventListener('mousedown', handleMouseDown);
        };
    })

    const getContributionColor = (level) => {
        if (level === 0) return 'transparent';
        const minColor = { r: 14, g: 68, b: 41 };   // #0e4429
        const maxColor = { r: 57, g: 211, b: 83 };  // #39d353
        const ratio = level / 10;
        const r = Math.round(minColor.r + (maxColor.r - minColor.r) * ratio);
        const g = Math.round(minColor.g + (maxColor.g - minColor.g) * ratio);
        const b = Math.round(minColor.b + (maxColor.b - minColor.b) * ratio);
        return `rgb(${r}, ${g}, ${b})`;
    };

    // Filter repos
    const starredList = userRepos ? userRepos.filter(repo => starredRepos?.includes(repo.id.toString())) : [];
    const otherList = userRepos ? userRepos.filter(repo => !starredRepos?.includes(repo.id.toString())) : [];

    const selectedRepoName = userRepos?.find(r => r.id.toString() === selectedRepo?.toString())?.name || 'Select a repository';

    return (
        <>
            <div className={`commit-tools ${hideTools ? 'hide' : ''}`}>
                <div 
                    className={`draw-options commit-border ${activeTooltip === 1 || activeTooltip === 2 ? 'active' : ''}`} 
                    ref={drawOptionsRef} 
                    onMouseDown={(e) => e.stopPropagation()}
                    onMouseEnter={() => handleMouseEnter(lastActiveDrawTool)}
                    onMouseLeave={handleMouseLeave}
                >
                    <div className="tool-size commit-border">
                        <div className="size-indicator">
                            <div
                                className="circle"
                                style={{
                                    height: `${circleSize}px`,
                                }}
                            ></div>
                        </div>
                        <input
                            className="size-input"
                            type="number"
                            value={circleSize === null ? '' : circleSize}
                            onChange={(e) => {
                                const value = e.target.value;
                                setCircleSize(value === '' ? null : Number(value));
                            }}
                            onBlur={() => {
                                if (circleSize === null || isNaN(circleSize)) {
                                    setCircleSize(1); // Reset to min value on blur
                                }
                            }}
                            min={1}
                        />
                    </div>
                    {lastActiveDrawTool === 1 && (
                        <div className="level-selector">
                            <div 
                                className="level-sq selected"
                                style={{ backgroundColor: getContributionColor(selectedLevel) }}
                            >
                                <div className="inner-text">{selectedLevel}</div>
                            </div>
                            <input 
                                type="range" 
                                min="1" 
                                max="10" 
                                value={selectedLevel} 
                                onChange={(e) => handleLevelClick(parseInt(e.target.value))}
                                className="commit-slider"
                            />
                        </div>
                    )}
                    {lastActiveDrawTool === 2 && (
                        <div className="clear-canvas" onClick={clearCanvas}>
                            <img src={trash} alt="" />
                        </div>
                    )}
                </div>

                <div className="tools commit-border commit-bg">
                    {TOOLS.map((tool, index) => (
                        <div
                            className={`tool${selectedTool === index ? ' selected' : ''}`}
                            key={index}
                            data-index={index}
                            onClick={() => handleToolClick(index)}
                            onMouseEnter={() => {
                                if (index === 1 || index === 2) {
                                    handleMouseEnter(index);
                                } else {
                                    // For cursor (0), maybe no tooltip? Or close others?
                                    handleMouseLeave(); 
                                }
                            }}
                            onMouseLeave={handleMouseLeave}
                        >
                            <img
                                src={tool}
                                alt={`Tool ${index}`}
                                className="tool-icon"
                            />
                        </div>
                    ))}
                </div>

                {/* Shuffle Button */}
                <div 
                    className="shuffle-container commit-border commit-bg"
                    onMouseEnter={() => handleMouseEnter('shuffle')}
                    onMouseLeave={handleMouseLeave}
                >
                    <div className="shuffle-btn">
                        <img src={shuffle} alt="Shuffle" onError={(e) => {e.target.style.display='none'; e.target.parentNode.innerText='Shuffle'}}/>
                    </div>
                    
                    <div 
                        className={`shuffle-options commit-border commit-bg ${activeTooltip === 'shuffle' ? 'active' : ''}`} 
                        ref={shuffleOptionsRef}
                        onMouseEnter={() => handleMouseEnter('shuffle')}
                        onMouseLeave={handleMouseLeave}
                    >
                        <div className="level-selector">
                            <div 
                                className="level-sq selected"
                                style={{ backgroundColor: getContributionColor(shuffleIntensity) }}
                            >
                                <div className="inner-text">1 - {shuffleIntensity}</div>
                            </div>
                            <input 
                                type="range" 
                                min="1" 
                                max="10" 
                                value={shuffleIntensity} 
                                onChange={(e) => setShuffleIntensity(parseInt(e.target.value))}
                                className="commit-slider"
                            />
                        </div>
                        <button className="apply-shuffle" onClick={applyShuffle}>Generate</button>
                    </div>
                </div>
                
                {/* Year selector dropdown */}
                <div className="year-selector commit-border commit-bg">
                    <select 
                        value={selectedYear || ''} 
                        onChange={handleYearChange}
                        className="year-dropdown"
                    >
                        {availableYears && availableYears.map((year) => (
                            <option key={year} value={year}>
                                {year}
                            </option>
                        ))}
                    </select>
                </div>
                
                <div className={`commit-changes commit-border commit-bg ${commitReady ? 'ready' : ''}`} onClick = {openConfirmation}>
                    make commits
                    <img src={commitReady ? arrow : lock} alt="" />
                </div>
            </div>


            <div className={`commit-confirmation ${hideConfirm ? 'hide' : ''}`}>
                <header>You are about to make <span>{formattedCommits?.length}</span> commits</header>
                <div className="content">
                    
                    <section>
                        <div className="top">
                            <p>Select a repository:</p>
                            <a>what is being committed?</a>
                        </div>
                        <div className="bottom" ref={repoDropdownRef}>
                            <div 
                                className={`custom-dropdown ${showRepoDropdown ? 'open' : ''}`} 
                                onClick={() => setShowRepoDropdown(!showRepoDropdown)}
                            >
                                <div className="selected-value">{selectedRepoName}</div>
                                <div className="arrow">▼</div>
                            </div>
                            
                            {showRepoDropdown && (
                                <div className="dropdown-list" onWheel={(e) => e.stopPropagation()}>
                                    {starredList.length > 0 && (
                                        <div className="dropdown-group">
                                            <div className="group-title">Starred</div>
                                            {starredList.map(repo => (
                                                <div 
                                                    key={repo.id} 
                                                    className={`dropdown-item ${selectedRepo === repo.id.toString() ? 'selected' : ''}`}
                                                    onClick={() => handleRepoSelect(repo.id.toString())}
                                                >
                                                    <span className="repo-name">
                                                        {repo.name} 
                                                        {repo.private && <span className="private-tag">Private</span>}
                                                    </span>
                                                    <span 
                                                        className="star-btn starred" 
                                                        onClick={(e) => handleUnstarRepo(e, repo.id)}
                                                        title="Unstar repository"
                                                    >
                                                        ★
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    
                                    <div className="dropdown-group">
                                        <div className="group-title">All Repositories</div>
                                        {otherList.map(repo => (
                                            <div 
                                                key={repo.id} 
                                                className={`dropdown-item ${selectedRepo === repo.id.toString() ? 'selected' : ''}`}
                                                onClick={() => handleRepoSelect(repo.id.toString())}
                                            >
                                                <span className="repo-name">
                                                    {repo.name} 
                                                    {repo.private && <span className="private-tag">Private</span>}
                                                </span>
                                                <span 
                                                    className="star-btn unstarred" 
                                                    onClick={(e) => handleStarRepo(e, repo.id)}
                                                    title="Star repository"
                                                >
                                                    ☆
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </section>

                    <section>
                        <div className="top">
                            <p>Estimated commit time: {((formattedCommits?.length * 0.8) / 60).toFixed(2)} minutes</p>
                        </div>
                        <div className="bottom">
                            <div className="confirm-btns">
                                <button className = "commit-confirm-btn" onClick={submit}>YES, make those commits</button>
                                <div className="cancel" onClick={openTools}>cancel</div>
                            </div>
                        </div>
                    </section>

                </div>
            </div>


            <div className="clear-confirmation">

            </div>

            <div className={`commit-loading ${hideLoading ? 'hide' : ''}`}>
                {
                    loadingDone ? 
                    <p><span>{loadingGood ? 'Committed!!! (๑•̀ㅂ•́)و✧ - Refresh the page to see Updates' : ''}</span></p>
                    :
                    <>
                        <p>YOO! We're making those commits for you, it'll take about <span>{((formattedCommits?.length * 0.8) / 60).toFixed(2)} minutes</span> to finish</p>
                        <div className="dot"></div>
                    </>
                }
            </div>
        </>
    );
}

export default CommitTools;